import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDataProvider, useGetIdentity, useGetList, useNotify, useRecordContext } from 'react-admin';
import { useQuery, useQueryClient } from 'react-query';
import { Loader2, RefreshCw, Sparkles, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { supabase } from '../../lib/supabaseClient';
import { getRefreshToken, getToken } from '../../authentication/authProvider';
import { Funeral } from '../../types';
import { createNote } from './notesApi';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

/**
 * Sidebar Notes card (T-045): every staff note on the case in one place, with
 * an AI "Key info" summary of them on top. The notes still show in the feed —
 * this is a second view and a second way to add one — and every note and
 * every summary bullet jumps to its spot in the feed.
 *
 * The summary is built by the backend (NestJS notes-summary) from the notes
 * plus three case fields, and only once there are 2+ sources: a single note is
 * its own summary.
 */

interface FeedNote {
  id: string;
  message: string | null;
  user_name?: string | null;
  timestamp: string;
  created_at?: string | null;
  updated_at?: string | null;
}

interface SummaryBullet {
  text: string;
  source_ids: string[];
}

interface NoteSummary {
  funeral_id: number;
  bullets: SummaryBullet[];
  source_count: number;
  generated_at: string | null;
  stale_at: string | null;
  last_error: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  'field:delivery_note': 'Delivery note',
  'field:crematorium_special_instructions': 'Note for crematorium',
  'field:internal_notes': 'Internal notes',
};
// Case fields shown in the delivery card; internal_notes lives only on the Edit form.
const FIELD_ANCHORS: Record<string, string> = {
  'field:delivery_note': 'delivery',
  'field:crematorium_special_instructions': 'delivery',
};
const MIN_SUMMARY_SOURCES = 2;

/**
 * The admin can deploy before the migration that adds funeral_note_summaries
 * and notes.deleted_at lands (CI migrations and the Railway deploy run side by
 * side). Until then the card still lists and adds notes; Key info, edit and
 * delete stay hidden.
 */
function isMissingSchema(error: { code?: string; message?: string }): boolean {
  return (
    ['42P01', '42703', 'PGRST204', 'PGRST205'].includes(error.code ?? '') ||
    /does not exist|could not find the table/i.test(error.message ?? '')
  );
}

interface SummaryState {
  /** False until the notes-summary migration is applied. */
  available: boolean;
  row: NoteSummary | null;
}
const NOTE_ID = /^[0-9a-f-]{36}(:\d+)?$/i;

// ─── Time labels ─────────────────────────────────────────────────────────────

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const firstName = (name?: string | null) => (name || 'Staff').trim().split(/\s+/)[0];

// ─── Jump to the source ─────────────────────────────────────────────────────

function flash(el: HTMLElement) {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const prev = el.style.boxShadow;
  el.style.transition = 'box-shadow 0.25s';
  el.style.boxShadow = '0 0 0 3px rgb(47 91 214), 0 0 0 7px rgb(47 91 214 / 0.18)';
  window.setTimeout(() => {
    el.style.boxShadow = prev;
  }, 2200);
}

function useJump() {
  const notify = useNotify();
  return (sourceId: string) => {
    const anchor = FIELD_ANCHORS[sourceId];
    const selector = anchor
      ? `[data-notes-anchor="${anchor}"]`
      : `[data-feed-note-id="${sourceId.replace(/"/g, '')}"]`;
    const el = document.querySelector<HTMLElement>(selector);
    if (el) flash(el);
    else notify('That note isn’t on screen in the feed.', { type: 'info' });
  };
}

// ─── Summary ─────────────────────────────────────────────────────────────────

function KeyInfo({
  summary,
  notesById,
  noteCount,
  fieldIds,
  onJump,
  onRegenerate,
  regenerating,
}: {
  summary: NoteSummary | null | undefined;
  notesById: Map<string, FeedNote>;
  noteCount: number;
  fieldIds: string[];
  onJump: (id: string) => void;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  const bullets = summary?.bullets ?? [];
  const updating = regenerating || !!summary?.stale_at;
  const fieldNames = fieldIds.map((f) => FIELD_LABELS[f]?.toLowerCase()).filter(Boolean);
  const from = [`${noteCount} note${noteCount === 1 ? '' : 's'}`, ...fieldNames].join(' + ');

  const sourceLabel = (id: string): string | null => {
    if (FIELD_LABELS[id]) return FIELD_LABELS[id];
    const n = notesById.get(id);
    return n ? `${firstName(n.user_name)} · ${relativeTime(n.timestamp)}` : null;
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
          <Sparkles className="h-3 w-3" />
          Key info
        </span>
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
          className="inline-flex items-center gap-1 rounded border border-amber-200 bg-white/60 px-1.5 py-0.5 text-[11px] text-amber-800 hover:bg-white disabled:opacity-60"
          title="Rebuild the summary from the notes now"
        >
          <RefreshCw className={cn('h-3 w-3', regenerating && 'animate-spin')} />
          Regenerate
        </button>
      </div>

      {bullets.length > 0 ? (
        <ul className="space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-[13px] leading-snug text-foreground">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-600" />
              <span>
                {b.text}{' '}
                {b.source_ids.map((id) => {
                  const label = sourceLabel(id);
                  if (!label) return null;
                  const linkable = !!FIELD_ANCHORS[id] || notesById.has(id);
                  return linkable ? (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onJump(id)}
                      className="mr-1 whitespace-nowrap border-b border-dotted border-current text-[11px] text-blue-700 hover:text-blue-900"
                    >
                      {label}
                    </button>
                  ) : (
                    <span key={id} className="mr-1 whitespace-nowrap text-[11px] text-muted-foreground">
                      {label}
                    </span>
                  );
                })}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12.5px] text-amber-900/80">
          {updating
            ? 'Summarizing the notes…'
            : summary?.last_error
              ? 'The summary couldn’t be built. Try Regenerate.'
              : 'No summary yet. Use Regenerate to build one.'}
        </p>
      )}

      <p className="text-[11px] text-amber-800/80">
        AI summary · from {from}
        {' · '}
        {updating ? 'Updating…' : summary?.generated_at ? `updated ${relativeTime(summary.generated_at)}` : 'not built yet'}
      </p>
    </div>
  );
}

// ─── One note ────────────────────────────────────────────────────────────────

function NoteRow({
  note,
  clamp,
  editable,
  onJump,
  onChanged,
}: {
  note: FeedNote;
  clamp?: boolean;
  editable?: boolean;
  onJump: (id: string) => void;
  onChanged: () => void;
}) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [mode, setMode] = useState<'view' | 'edit' | 'confirm-delete'>('view');
  const [draft, setDraft] = useState(note.message ?? '');
  const [saving, setSaving] = useState(false);
  // Lead notes mirrored onto the case carry "<note id>:<funeral id>" in the feed.
  const noteId = note.id.split(':')[0];
  const canEdit = editable && NOTE_ID.test(note.id);
  const edited =
    !!note.updated_at &&
    !!note.created_at &&
    new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 1000;

  const save = async (data: Record<string, unknown>) => {
    setSaving(true);
    try {
      await dataProvider.update('notes', { id: noteId, data, previousData: { id: noteId } });
      setMode('view');
      onChanged();
    } catch (err: any) {
      notify(`Couldn't save the note: ${err?.message || 'unknown error'}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="space-y-1.5 border-t border-border py-2 first:border-t-0">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          disabled={saving}
          className="w-full resize-y rounded-md border bg-background px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="flex justify-end gap-2 text-[12px]">
          <button type="button" className="text-muted-foreground" onClick={() => setMode('view')}>
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || !draft.trim()}
            className="font-semibold text-blue-700 disabled:opacity-50"
            onClick={() => void save({ message: draft.trim() })}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group border-t border-border py-2 first:border-t-0">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[9px] font-semibold text-foreground/70">
          {firstName(note.user_name)[0]}
        </span>
        <span className="font-medium text-foreground/80">{note.user_name || 'Staff'}</span>
        <span>· {relativeTime(note.timestamp)}</span>
        {edited && <span>· edited</span>}
        <span className="ml-auto flex gap-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {canEdit && mode === 'view' && (
            <>
              <button type="button" className="hover:text-foreground" onClick={() => { setDraft(note.message ?? ''); setMode('edit'); }}>
                Edit
              </button>
              <button type="button" className="hover:text-destructive" onClick={() => setMode('confirm-delete')}>
                Delete
              </button>
            </>
          )}
          <button type="button" className="text-blue-700 hover:text-blue-900" onClick={() => onJump(note.id)}>
            Show in feed ↗
          </button>
        </span>
      </div>
      <button
        type="button"
        onClick={() => onJump(note.id)}
        className={cn(
          'mt-0.5 block w-full whitespace-pre-wrap text-left text-[13px] leading-snug text-foreground',
          clamp && 'line-clamp-2',
        )}
      >
        {note.message}
      </button>
      {mode === 'confirm-delete' && (
        <div className="mt-1.5 flex items-center justify-between rounded-md bg-destructive/10 px-2 py-1 text-[12px]">
          <span>Delete this note for everyone?</span>
          <span className="flex gap-3">
            <button type="button" className="text-muted-foreground" onClick={() => setMode('view')}>
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              className="font-semibold text-destructive"
              onClick={() => void save({ deleted_at: new Date().toISOString() })}
            >
              Delete
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

/**
 * Rendered as the sidebar accordion's "notes" item, like its sibling sections.
 * `onOpen` lets "+ Add" open the section when it is collapsed; `onEmpty` is
 * called once, when the notes first load and there are none, so the sidebar
 * can start an empty Notes section collapsed.
 */
export function NotesCard({ onOpen, onEmpty }: { onOpen?: () => void; onEmpty?: () => void } = {}) {
  const record = useRecordContext<Funeral>();
  const funeralId = record?.id as number | undefined;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const jump = useJump();

  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: notes = [], isSuccess: notesLoaded } = useGetList<FeedNote>(
    'funeral_feed',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'timestamp', order: 'DESC' },
      filter: { funeral_id: funeralId, type: 'internal_note' },
    },
    { enabled: !!funeralId },
  );

  const summaryKey = ['funeral_note_summaries', funeralId];
  const { data: summaryState } = useQuery<SummaryState>(
    summaryKey,
    async () => {
      const { data, error } = await supabase
        .from('funeral_note_summaries')
        .select('funeral_id, bullets, source_count, generated_at, stale_at, last_error')
        .eq('funeral_id', funeralId)
        .maybeSingle();
      if (error) {
        if (isMissingSchema(error)) return { available: false, row: null };
        throw error;
      }
      return { available: true, row: data as NoteSummary | null };
    },
    {
      enabled: !!funeralId,
      // While a rebuild is pending check back until it lands (the sweep waits
      // for 60s of quiet, then Haiku takes a few seconds).
      refetchInterval: (data) => (data?.row?.stale_at ? 15_000 : false),
    },
  );

  const summary = summaryState?.row ?? null;
  const migrated = !!summaryState?.available;

  const notesById = useMemo(() => new Map(notes.map((n) => [String(n.id), n])), [notes]);
  const fieldIds = useMemo(
    () =>
      (['delivery_note', 'crematorium_special_instructions', 'internal_notes'] as const)
        .filter((f) => ((record as any)?.[f] ?? '').trim())
        .map((f) => `field:${f}`),
    [record],
  );
  const sourceCount = notes.filter((n) => (n.message ?? '').trim()).length + fieldIds.length;
  // A case with no notes shows just the header, whatever its fields hold. The
  // backend also drops routine agent logs (transport acks and the like); when
  // that leaves under 2 sources it builds no summary, and neither do we. Nor
  // when the model read the notes and found nothing still worth keeping.
  const nothingToSummarize =
    !!summary &&
    !summary.stale_at &&
    (summary.generated_at
      ? !summary.bullets?.length && !summary.last_error
      : summary.source_count < MIN_SUMMARY_SOURCES);
  const showSummary =
    migrated && notes.length > 0 && sourceCount >= MIN_SUMMARY_SOURCES && !nothingToSummarize;
  const latest = notes[0];

  // An empty case starts collapsed (no notes means no Key info either). Only
  // on the first load, so it never closes a section someone opened.
  const emptyChecked = useRef(false);
  useEffect(() => {
    if (!notesLoaded || emptyChecked.current) return;
    emptyChecked.current = true;
    if (notes.length === 0) onEmpty?.();
  }, [notesLoaded, notes.length, onEmpty]);

  useEffect(() => {
    if (expanded) composerRef.current?.focus();
  }, [expanded]);

  const refresh = () => {
    void queryClient.invalidateQueries(['funeral_feed']);
    void queryClient.invalidateQueries(summaryKey);
  };

  const addNote = async () => {
    const message = draft.trim();
    if (!message || !funeralId) return;
    setAdding(true);
    try {
      await createNote({ dataProvider, message, funeralId, userName: identity?.fullName || undefined });
      setDraft('');
      refresh();
    } catch (err: any) {
      notify(`Couldn't add the note: ${err?.message || 'unknown error'}`, { type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  const regenerate = async () => {
    if (!funeralId) return;
    setRegenerating(true);
    try {
      const res = await fetch(`${PAPERMAN_API_URL}notes-summary/${funeralId}/regenerate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getToken()}`,
          'Refresh-Token': await getRefreshToken(),
        },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.message || `status ${res.status}`);
      }
      queryClient.setQueryData<SummaryState>(summaryKey, { available: true, row: await res.json() });
    } catch (err: any) {
      notify(`Couldn't regenerate the summary: ${err?.message || 'unknown error'}`, { type: 'error' });
    } finally {
      setRegenerating(false);
    }
  };

  if (!record) return null;

  const grouped: Array<{ day: string; items: FeedNote[] }> = [];
  for (const n of notes) {
    const day = dayLabel(n.timestamp);
    if (grouped[grouped.length - 1]?.day !== day) grouped.push({ day, items: [] });
    grouped[grouped.length - 1].items.push(n);
  }

  return (
    <AccordionItem value="notes">
      <AccordionTrigger
        icon={<StickyNote className="h-4 w-4" />}
        action={
          expanded ? (
            <button type="button" className="text-[12px] font-medium text-blue-700" onClick={() => setExpanded(false)}>
              Show less
            </button>
          ) : (
            <button
              type="button"
              className="text-[12px] font-semibold text-blue-700"
              onClick={() => {
                onOpen?.();
                setExpanded(true);
              }}
            >
              + Add
            </button>
          )
        }
      >
        <span className="flex items-center gap-2">
          Notes
          {notes.length > 0 && (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[11px] font-semibold leading-4 text-amber-800">
              {notes.length}
            </span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className="space-y-2.5">
        {!expanded && notes.length === 0 && (
          <p className="text-[12.5px] text-muted-foreground">No notes yet.</p>
        )}

        {showSummary && (
          <KeyInfo
            summary={summary}
            notesById={notesById}
            noteCount={notes.length}
            fieldIds={fieldIds}
            onJump={jump}
            onRegenerate={() => void regenerate()}
            regenerating={regenerating}
          />
        )}

        {expanded && (
          <div className="flex items-end gap-1.5 rounded-md border bg-muted/30 py-1.5 pl-2.5 pr-1.5">
            <textarea
              ref={composerRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void addNote();
                }
              }}
              rows={draft.includes('\n') ? 3 : 1}
              disabled={adding}
              placeholder="Add a note for the team…"
              aria-label="New note"
              className="min-h-[20px] flex-1 resize-none bg-transparent text-[13px] placeholder:text-muted-foreground focus:outline-none"
            />
            <button
              type="button"
              disabled={adding || !draft.trim()}
              onClick={() => void addNote()}
              className="inline-flex items-center gap-1 rounded bg-foreground px-2.5 py-1 text-[12px] font-semibold text-background disabled:opacity-40"
            >
              {adding && <Loader2 className="h-3 w-3 animate-spin" />}
              Add
            </button>
          </div>
        )}

        {!expanded && latest && (
          <div>
            {showSummary && (
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                Latest note
              </div>
            )}
            <div>
              {/* A lone note has no "Show all" to open it, so it shows in full. */}
              <NoteRow note={latest} clamp={notes.length > 1} onJump={jump} onChanged={refresh} />
            </div>
            {notes.length > 1 && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="mt-1 text-[12.5px] font-semibold text-blue-700"
              >
                Show all ({notes.length})
              </button>
            )}
          </div>
        )}

        {expanded &&
          grouped.map((g) => (
            <div key={g.day}>
              <div className="pt-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                {g.day}
              </div>
              {g.items.map((n) => (
                <NoteRow key={n.id} note={n} editable={migrated} onJump={jump} onChanged={refresh} />
              ))}
            </div>
          ))}
      </AccordionContent>
    </AccordionItem>
  );
}

export default NotesCard;
